/** biome-ignore-all lint/suspicious/noExplicitAny: no any */
import React, {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useMemo,
  useState,
} from 'react';
import {useSelector} from 'react-redux';
import {selectBackgroundColor} from '~/common/fabric/reducers/fabricDesign/selectors';
import {useVideoControl} from '~/common/hooks/useVideoControls';

type FabricCanvas = any;
type FabricObject = any;

type VideoControlType = ReturnType<typeof useVideoControl>;

interface EventCreateContextType {
  canvas: FabricCanvas | null;
  setCanvas: Dispatch<SetStateAction<FabricCanvas | null>>;
  cardDesignRef: React.RefObject<HTMLDivElement> | null;
  setCardDesignRef: Dispatch<SetStateAction<React.RefObject<HTMLDivElement> | null>>;
  activeObjects: FabricObject[];
  setActiveObjects: Dispatch<SetStateAction<FabricObject[]>>;
  effectVideo: VideoControlType;
  revealVideo: VideoControlType;
  bkgColor: string;
  setBkgColor: Dispatch<SetStateAction<string>>;
  processing: boolean;
  setProcessing: Dispatch<SetStateAction<boolean>>;
}

const defaultContextValue: EventCreateContextType = {
  canvas: null,
  setCanvas: () => null,
  cardDesignRef: null,
  setCardDesignRef: () => null,
  activeObjects: [],
  setActiveObjects: () => null,
  effectVideo: null as unknown as VideoControlType,
  revealVideo: null as unknown as VideoControlType,
  bkgColor: '#c0c0c0',
  setBkgColor: () => null,
  processing: false,
  setProcessing: () => null,
};

export const EventCreateContext = createContext<EventCreateContextType>(defaultContextValue);

interface EventCreateParentProps {
  children: ReactNode;
}

export const EventCreateParent = ({children}: EventCreateParentProps) => {
  const [canvas, setCanvas] = useState<FabricCanvas | null>(null);
  const [cardDesignRef, setCardDesignRef] = useState<React.RefObject<HTMLDivElement> | null>(null);
  const [activeObjects, setActiveObjects] = useState<FabricObject[]>([]);
  const backgroundColor = useSelector(selectBackgroundColor);
  const [bkgColor, setBkgColor] = useState<string>(backgroundColor || '#c0c0c0');
  const effectVideo = useVideoControl();
  const revealVideo = useVideoControl();
  const [processing, setProcessing] = useState<boolean>(false);

  const context = useMemo<EventCreateContextType>(
    () => ({
      canvas,
      setCanvas,
      activeObjects,
      setActiveObjects,
      cardDesignRef,
      setCardDesignRef,
      effectVideo,
      revealVideo,
      bkgColor,
      setBkgColor,
      processing,
      setProcessing,
    }),
    [canvas, activeObjects, cardDesignRef, effectVideo, revealVideo, bkgColor, processing]
  );

  return <EventCreateContext.Provider value={context}>{children}</EventCreateContext.Provider>;
};
